/* El par que sigue · SOP-carrito.md §4.6 y §4.8
 *
 * La tarjeta que, debajo de las líneas del carrito, ofrece OTRO PAR en la talla que el comprador
 * ya escogió, con lo que gana al sumarlo. En Audor no hay complementos que vender —el catálogo
 * entero es calzado—, así que la oferta es el escalón siguiente de la escalera.
 *
 * NO MIRA EL CARRITO POR SU CUENTA. `aud-carrito.js` ya lo lee, ya sabe cuántos pares cuentan y
 * ya hizo la resta del ahorro; aquí se escuchan sus tres avisos:
 *   aud:carrito:monta   → llegó el carrito. Se decide qué ofrecer y se pinta.
 *   aud:carrito:coloca  → Horizon repintó. La tarjeta vuelve a su sitio, sin esperar a la red.
 *   aud:carrito:vacia   → no queda nada dentro. Se olvida todo.
 *
 * LAS CINCO COMPUERTAS (SOP §4.8). Un candidato se ofrece solo si:
 *   G-1  no está ya en el carrito,
 *   G-2  tiene la MISMA TALLA que la línea de referencia, y está disponible,
 *   G-3  ESTÁ EN LA COLECCIÓN DE LA OFERTA (D-073) — si no, el ahorro anunciado sería falso.
 *        Los hermanos traen sus colecciones desde Liquid; las recomendaciones nativas no, y
 *        se filtran con el precio y las dos llaves de la colección en espejo (título con «suela
 *        liviana», etiqueta `car-suela-liviana`),
 *   G-4  es del mismo género, leído del título,
 *   G-5  y si además comparte el número de Ref, se presenta como «el mismo modelo». Si no lo
 *        comparte NO se descarta: baja de categoría y se ofrece como «otro modelo», sin afirmar
 *        horma. Medido el 2026-09-28: la Ref 1200 Negra tiene de hermanos a la familia 507.
 *
 * TODA ESCRITURA COMPARA ANTES DE ESCRIBIR. El cajón está vigilado: escribir lo mismo dos veces
 * sería pedir el carrito dos veces.
 */

const CAJA = document.getElementById('aud-carrito-datos');
const D = CAJA ? JSON.parse(CAJA.textContent) : null;
const S = D && D.sug;

if (S && S.mostrar) {
  const SUAVE = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const RAIZ = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const MAS =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" aria-hidden="true">' +
    '<path d="M12 5v14M5 12h14"/></svg>';
  const ORDINALES = String(S.ordinales || '').split(',').map((o) => o.trim()).filter(Boolean);

  let pieza = null;      /* la tarjeta, por referencia */
  let lista = [];        /* los candidatos que pasaron las compuertas */
  let idx = 0;           /* cuál se está viendo */
  let clave = '';        /* de qué carrito salió `lista` */
  let turno = 0;         /* para descartar respuestas que llegan tarde */
  let ultimo = null;     /* el último aviso de montaje */
  let ocupado = false;

  /* ---------- leer un título ----------
     Las 54 publicaciones llevan el patrón «… Ref- 1200 Negra», con y sin espacio tras el guion.
     Una sola —la 609— no lleva «Ref»: para ella vale el primer número de tres o cuatro cifras. */
  function refDe(titulo) {
    const m = /ref\W*(\d+)/i.exec(titulo) || /\b(\d{3,4})\b/.exec(titulo);
    return m ? m[1] : '';
  }

  function generoDe(titulo) {
    if (/mujer|dama/i.test(titulo)) return 'mujer';
    if (/hombre|caballero/i.test(titulo)) return 'hombre';
    return '';
  }

  /* «Bota Hombre Outdoor» arriba y «REF 507 · CAFE» debajo: lo mismo que pintan las tarjetas
     de la reja (`aud-card-nombre` y `aud-card-ref`), para que la bota se llame igual aquí. */
  function partes(titulo) {
    const trozos = String(titulo).split(/ref\s*-/i);
    if (trozos.length < 2) return { nombre: String(titulo).trim(), ref: '' };
    const cola = trozos[trozos.length - 1].trim().toUpperCase().split(/\s+/);
    const numero = cola.shift() || '';
    return {
      nombre: trozos[0].trim() || String(titulo).trim(),
      ref: numero ? 'REF ' + numero + (cola.length ? ' · ' + cola.join(' ') : '') : '',
    };
  }

  function tallaDe(item) {
    const op = (item.options_with_values || []).find((o) => /talla/i.test(o.name));
    return String((op && op.value) || item.variant_title || '').trim();
  }

  /* ---------- de dónde salen los candidatos ---------- */
  const memoHermanos = new Map();
  const memoOtros = new Map();

  function hermanosDe(handle) {
    if (!memoHermanos.has(handle)) {
      memoHermanos.set(
        handle,
        fetch(RAIZ + 'products/' + handle + '?section_id=aud-hermanos')
          .then((r) => (r.ok ? r.text() : ''))
          .then((html) => {
            const n = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-aud-hermanos]');
            return n ? JSON.parse(n.textContent).candidatos || [] : [];
          })
          .catch(() => [])
      );
    }
    return memoHermanos.get(handle);
  }

  /* Las recomendaciones nativas de Shopify, puestas en la misma forma que los hermanos. */
  function otrosDe(productId) {
    const id = String(productId);
    if (!memoOtros.has(id)) {
      memoOtros.set(
        id,
        fetch(RAIZ + 'recommendations/products.json?product_id=' + id + '&limit=10&intent=related')
          .then((r) => (r.ok ? r.json() : { products: [] }))
          .then((d) =>
            (d.products || []).map((p) => ({
              id: p.id,
              handle: p.handle,
              titulo: p.title,
              url: p.url,
              foto: p.featured_image || null,
              tags: p.tags || [],
              variantes: (p.variants || []).map((v) => ({
                id: v.id,
                talla: v.title,
                disponible: v.available,
                precio: v.price,
              })),
            }))
          )
          .catch(() => [])
      );
    }
    return memoOtros.get(id);
  }

  async function buscar(base, carrito, d) {
    const cuenta = d.cuenta;
    /* G-3. Con colecciones a la vista manda la colección; sin ellas, la reserva en espejo. */
    const admite = (c, v) => {
      if (d.oferta && Array.isArray(c.colecciones)) return c.colecciones.includes(d.oferta);
      if (!cuenta(v.precio)) return false;
      if (/suela liviana/i.test(c.titulo || '')) return false;
      return !(c.tags || []).some((t) => String(t).toLowerCase() === 'car-suela-liviana');
    };
    const talla = tallaDe(base);
    const dentro = new Set((carrito.items || []).map((it) => String(it.product_id)));
    const genero = generoDe(base.product_title || base.title || '');
    const ref = refDe(base.product_title || base.title || '');

    const pasa = (c) => {
      if (!c || dentro.has(String(c.id))) return null;                       /* G-1 */
      const v = (c.variantes || []).find(
        (x) => String(x.talla).trim() === talla && x.disponible                 /* G-2 */
      );
      if (!v || !admite(c, v)) return null;                                    /* G-3 */
      const g = generoDe(c.titulo || '');
      if (!genero || !g || g !== genero) return null;                          /* G-4 */
      const mismo = !!ref && refDe(c.titulo || '') === ref;                    /* G-5 */
      return Object.assign({}, c, { variante: v, talla: talla, mismoModelo: mismo });
    };

    const vistos = new Set();
    const unicos = (arr) => arr.filter((c) => !vistos.has(String(c.id)) && vistos.add(String(c.id)));

    let buenos = unicos((await hermanosDe(base.handle)).map(pasa).filter(Boolean));
    if (!buenos.length && S.otros) {
      buenos = unicos((await otrosDe(base.product_id)).map(pasa).filter(Boolean));
    }
    /* El mismo modelo, primero. `sort` es estable: dentro de cada grupo se respeta el orden. */
    buenos.sort((a, b) => Number(b.mismoModelo) - Number(a.mismoModelo));
    return buenos.slice(0, Math.max(1, S.maximo || 3));
  }

  /* ---------- escribir sin repetir ---------- */
  function pon(el, valor) {
    if (el && el.textContent !== valor) el.textContent = valor;
  }

  function ponAttr(el, nombre, valor) {
    if (el && el.getAttribute(nombre) !== valor) el.setAttribute(nombre, valor);
  }

  function ponHtml(el, html) {
    if (el && el.dataset.html !== html) {
      el.innerHTML = html;
      el.dataset.html = html;
    }
  }

  function oculta(el, si) {
    if (el && el.hidden !== si) el.hidden = si;
  }

  /* Los hermanos ya traen su ancho desde Liquid; las recomendaciones traen la foto entera. */
  function anchoFoto(url) {
    if (!url) return '';
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=240';
  }

  /* ---------- la tarjeta ---------- (SOP §4.11)
     UNA PISTA QUE SE DESLIZA, NO UNA TARJETA QUE CAMBIA DE CARA (2026-09-29). Antes había una
     sola tarjeta y al tocar un punto se le cambiaba el contenido: los puntos decían «hay más
     a la derecha» y lo que pasaba era un parpadeo. Ahora las opciones están una al lado de la
     otra dentro de una pista con `scroll-snap`, y pasar de una a otra ES desplazarse hacia la
     derecha. Eso trae gratis lo que antes había que programar: el arrastre con el dedo, la
     inercia, y que el punto encendido sea siempre el de la tarjeta que se está viendo. */
  const TARJETA =
    '<a class="aud-cj-sug__foto" data-enlace tabindex="-1" aria-hidden="true">' +
      '<img data-foto alt="" width="64" height="64" loading="lazy" decoding="async">' +
    '</a>' +
    '<div class="aud-cj-sug__texto">' +
      '<div class="aud-cj-sug__alto">' +
        '<a class="aud-cj-sug__nombre" data-enlace data-nombre></a>' +
        '<span class="aud-cj-sug__precio" data-precio></span>' +
      '</div>' +
      '<span class="aud-cj-sug__ref" data-ref></span>' +
      '<p class="aud-cj-sug__nota" data-nota></p>' +
      '<p class="aud-cj-sug__oferta" data-oferta></p>' +
    '</div>' +
    '<button class="aud-cj-sug__boton" type="button" data-aud-agregar></button>' +
    '<p class="aud-cj-sug__error" role="alert" data-error hidden></p>';

  function armar() {
    /* Etiqueta propia, no `div`: así el morph de Horizon no puede confundirla con una pieza
       suya y quedársela (lección L-19 del SOP). */
    const el = document.createElement('aud-cj-par');
    el.className = 'aud-cj-sug';
    el.setAttribute('data-aud-carrito', 'sug');
    el.setAttribute('data-skip-node-update', '');
    el.innerHTML =
      '<p class="aud-cj-sug__titular" data-tit></p>' +
      '<div class="aud-cj-sug__pista" data-pista></div>' +
      '<div class="aud-cj-sug__puntos" data-puntos hidden></div>';

    /* El punto encendido sigue a la pista, venga el movimiento de un punto, del dedo o del
       teclado. Un cuadro de margen: `scroll` dispara muchas veces por segundo. */
    let pendiente = false;
    el.querySelector('[data-pista]').addEventListener('scroll', () => {
      if (pendiente) return;
      pendiente = true;
      const sigue = () => { pendiente = false; marcarPunto(); };
      if (document.hidden || typeof requestAnimationFrame !== 'function') setTimeout(sigue, 0);
      else requestAnimationFrame(sigue);
    }, { passive: true });
    return el;
  }

  function titular(total) {
    const ord = ORDINALES[total - 1];
    return ord ? ultimo.texto(S.titular, { ordinal: ord, n: total + 1 }) : S.titularOtro || '';
  }

  const tarjetas = () => (pieza ? [...pieza.querySelectorAll('[data-tarjeta]')] : []);

  /* Dónde tiene que quedar la pista para que la tarjeta `i` esté a la vista. */
  function sitioDe(i) {
    const ts = tarjetas();
    return ts[i] && ts[0] ? ts[i].offsetLeft - ts[0].offsetLeft : 0;
  }

  /* Cuál es la tarjeta que se está viendo: la que tiene su borde más cerca del de la pista. */
  function marcarPunto() {
    const pista = pieza && pieza.querySelector('[data-pista]');
    if (!pista) return;
    let cerca = 0;
    let menor = Infinity;
    tarjetas().forEach((t, i) => {
      const lejos = Math.abs(sitioDe(i) - pista.scrollLeft);
      if (lejos < menor) { menor = lejos; cerca = i; }
    });
    idx = cerca;
    pieza.querySelectorAll('[data-aud-punto]').forEach((b, i) => {
      ponAttr(b, 'aria-current', String(i === idx));
    });
  }

  function llenar(tarjeta, c) {
    const d = ultimo;
    const p = partes(c.titulo);
    const destino = c.url + (String(c.url).indexOf('?') >= 0 ? '&' : '?') + 'variant=' + c.variante.id;
    tarjeta.querySelectorAll('[data-enlace]').forEach((a) => ponAttr(a, 'href', destino));
    const src = anchoFoto(c.foto);
    if (src) ponAttr(tarjeta.querySelector('[data-foto]'), 'src', src);
    oculta(tarjeta.querySelector('.aud-cj-sug__foto'), !src);

    pon(tarjeta.querySelector('[data-nombre]'), p.nombre);
    pon(tarjeta.querySelector('[data-precio]'), d.pesos(c.variante.precio));
    pon(tarjeta.querySelector('[data-ref]'), p.ref);
    pon(
      tarjeta.querySelector('[data-nota]'),
      d.texto(c.mismoModelo ? S.notaHermano : S.notaOtro, { talla: c.talla })
    );

    /* El ahorro se calla si el descuento todavía no cobra lo prometido (compuerta del §3.2). */
    const anuncia = !d.reducido && d.gana > 0;
    const frase = d.pares >= 2 ? S.oferta : S.ofertaPrimera;
    pon(
      tarjeta.querySelector('[data-oferta]'),
      anuncia ? d.texto(frase, { delta: d.pesos(d.gana), talla: c.talla }) : ''
    );
    ponHtml(
      tarjeta.querySelector('[data-aud-agregar]'),
      MAS + '<span>' + d.limpiar(d.texto(S.boton, { talla: c.talla })) + '</span>'
    );
  }

  function pintar() {
    if (!pieza || !ultimo || !lista.length) return;
    const d = ultimo;

    pon(pieza.querySelector('[data-tit]'), titular(d.carrito.item_count || 0));

    /* Las tarjetas se arman una vez por lista. Si la lista es la misma —cambió la cantidad,
       no los productos— solo se les ponen al día los textos. */
    const pista = pieza.querySelector('[data-pista]');
    const firma = lista.map((c) => c.id + ':' + c.variante.id).join(',');
    if (pista.dataset.firma !== firma) {
      pista.innerHTML = lista
        .map((c, i) =>
          '<div class="aud-cj-sug__tarjeta" data-tarjeta data-i="' + i + '" role="group" aria-label="' +
          d.limpiar(d.texto(S.punto, { n: i + 1 })).replace(/"/g, '&quot;') + '">' + TARJETA + '</div>'
        )
        .join('');
      pista.dataset.firma = firma;
      pista.scrollLeft = 0;
      idx = 0;
    }
    tarjetas().forEach((t, i) => llenar(t, lista[i]));

    const puntos = pieza.querySelector('[data-puntos]');
    oculta(puntos, lista.length < 2);
    ponHtml(
      puntos,
      lista.length < 2
        ? ''
        : lista
            .map((_, i) =>
              '<button type="button" data-aud-punto="' + i + '" aria-label="' +
              d.limpiar(d.texto(S.punto, { n: i + 1 })).replace(/"/g, '&quot;') + '"></button>'
            )
            .join('')
    );
    puntos.querySelectorAll('[data-aud-punto]').forEach((b, i) => {
      ponAttr(b, 'aria-current', String(i === idx));
    });
  }

  /* Ir a una tarjeta es desplazar la pista hasta ella. Sin animar si el teléfono pide menos
     movimiento, o si la pestaña está oculta y la animación no llegaría a correr. */
  function ver(i) {
    if (!pieza || !lista.length) return;
    const pista = pieza.querySelector('[data-pista]');
    const destino = Math.max(0, Math.min(lista.length - 1, i));
    const suave = SUAVE && !document.hidden;
    /* `instant`, no `auto`: `auto` es «lo que diga la hoja», y no es lo mismo que «sin animar». */
    pista.scrollTo({ left: sitioDe(destino), behavior: suave ? 'smooth' : 'instant' });
    if (!suave) marcarPunto();
  }

  /* La tarjeta cierra la lista: va después de las líneas. */
  function colocar(sitio) {
    if (!pieza || !lista.length || !sitio) return;
    if (pieza.parentNode !== sitio || sitio.lastElementChild !== pieza) sitio.appendChild(pieza);
  }

  function retirar() {
    if (pieza && pieza.isConnected) pieza.remove();
  }

  /* ---------- los tres avisos ---------- */
  document.addEventListener('aud:carrito:monta', async (e) => {
    const d = e.detail;
    ultimo = d;
    const items = (d.carrito && d.carrito.items) || [];
    /* La línea de referencia: la última que entró y que cuenta para la escalera. */
    const base = items.find((it) => (d.entra ? d.entra(it) : d.cuenta(it.original_price)));
    if (!base) {
      lista = [];
      clave = '';
      retirar();
      return;
    }

    const nueva = base.variant_id + '|' + items.map((it) => it.product_id).sort().join(',');
    if (nueva !== clave) {
      const mio = ++turno;
      const hallados = await buscar(base, d.carrito, d);
      if (mio !== turno) return;
      clave = nueva;
      lista = hallados;
      idx = 0;
    }

    if (!lista.length) {
      retirar();
      return;
    }
    if (!pieza) pieza = armar();
    pintar();
    const sitio = d.lista && d.lista.isConnected
      ? d.lista
      : document.querySelector('#cart-drawer .cart-drawer__items');
    colocar(sitio);
  });

  document.addEventListener('aud:carrito:coloca', (e) => colocar(e.detail && e.detail.lista));

  document.addEventListener('aud:carrito:vacia', () => {
    lista = [];
    clave = '';
    turno += 1;
    retirar();
  });

  /* ---------- agregar ----------
     No hay paso de cantidad nativo que pulsar —es otro producto—, así que aquí sí se llama al
     carrito. Pero el REPINTADO sigue siendo de Horizon: se despacha el mismo
     `CartLinesUpdateEvent` que usa la ficha, con la promesa que resuelve en las secciones ya
     renderizadas. El cajón, la burbuja del encabezado y el medidor se enteran por el camino
     de siempre, y el día que Horizon cambie cómo repinta, esto sigue funcionando. */
  async function agregar(c, boton) {
    if (ocupado) return;
    ocupado = true;
    boton.disabled = true;
    boton.setAttribute('aria-busy', 'true');
    const tarjeta = boton.closest('[data-tarjeta]') || pieza;
    const error = tarjeta.querySelector('[data-error]');
    oculta(error, true);

    const rutas = (window.Theme && window.Theme.routes) || {};
    const leerCarrito = () =>
      fetch((rutas.cart_url || RAIZ + 'cart') + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json());
    const secciones = [...document.querySelectorAll('cart-items-component')]
      .map((n) => n.dataset.sectionId)
      .filter(Boolean);

    let Evento = null;
    try {
      Evento = (await import('@shopify/events')).CartLinesUpdateEvent || null;
    } catch (err) {
      /* sin el módulo se agrega igual; el cajón se pone al día por el observador */
    }
    const diferido = Evento && Evento.createPromise ? Evento.createPromise() : null;
    if (diferido) {
      pieza.dispatchEvent(
        new Evento({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: String(c.variante.id), quantity: 1 }],
          promise: diferido.promise,
        })
      );
    }

    try {
      const resp = await fetch(RAIZ + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: c.variante.id, quantity: 1 }],
          sections: secciones.join(','),
          sections_url: window.location.pathname,
        }),
      }).then((r) => r.json());

      const fallo = !!resp.status;
      const carrito = await leerCarrito();
      if (diferido) {
        diferido.resolve({
          cart: Evento.createCartFromAjaxResponse(carrito),
          detail: {
            items: carrito.items,
            source: 'aud-carrito-sug',
            itemCount: 1,
            sections: fallo ? undefined : resp.sections,
            didError: fallo,
          },
        });
      }
      if (fallo) {
        /* El motivo lo da Shopify, en el idioma de la tienda: casi siempre, que se agotó. */
        pon(error, String(resp.description || resp.message || S.error || ''));
        oculta(error, !error.textContent);
        memoHermanos.clear();
        memoOtros.clear();
        clave = '';
      }
    } catch (err) {
      if (diferido) diferido.reject(err);
      pon(error, String(S.error || ''));
      oculta(error, !error.textContent);
    } finally {
      ocupado = false;
      boton.disabled = false;
      boton.removeAttribute('aria-busy');
    }
  }

  /* Si el carrito se montó antes de que este archivo llegara, se pide el aviso otra vez. */
  document.dispatchEvent(new CustomEvent('aud:carrito:pide'));

  document.addEventListener('click', (e) => {
    if (!pieza || !pieza.contains(e.target)) return;
    const punto = e.target.closest('[data-aud-punto]');
    if (punto) {
      ver(Number(punto.dataset.audPunto));
      return;
    }
    const boton = e.target.closest('[data-aud-agregar]');
    if (!boton || !lista.length) return;
    /* La tarjeta del botón, no la que esté a la vista: con el teclado se puede llegar a un
       botón que todavía no se ha deslizado hasta el centro. */
    const tarjeta = boton.closest('[data-tarjeta]');
    const cual = lista[Number(tarjeta && tarjeta.dataset.i) || 0];
    if (cual) agregar(cual, boton);
  });
}
